<template>
  <div class="rx-phone">
    <header class="rx-phone__header">
      <div v-if="selectMode" class="rx-phone__bar">
        <h1 class="rx-phone__title rx-phone__title--root" aria-live="polite">{{ selectedCount }} selected</h1>
        <button type="button" class="rx-phone__select rx-phone__select--on" @click="$emit('select-cancel')">Cancel</button>
      </div>
      <div v-else class="rx-phone__bar">
        <button
          v-if="!isRoot"
          type="button"
          class="rx-phone__iconbtn"
          aria-label="Back to parent folder"
          @click="$emit('back')">
          <i class="fas fa-chevron-left"></i>
        </button>
        <h1 :class="['rx-phone__title', isRoot ? 'rx-phone__title--root' : '']" :title="currentNode.name">{{ currentNode.name }}</h1>
        <button type="button" class="rx-phone__iconbtn" aria-label="Search folders" @click="$emit('open-jump', true)">
          <i class="fas fa-search"></i>
        </button>
        <button type="button" class="rx-phone__iconbtn" aria-label="More options" @click="$emit('overflow')">
          <i class="fas fa-ellipsis-v"></i>
        </button>
        <button
          type="button"
          class="rx-phone__select rx-phone__select--on"
          :disabled="!hasSelectable"
          @click="$emit('select-start', null)">Select</button>
      </div>
      <button type="button" class="rx-phone__path" @click="$emit('open-jump', false)" :aria-label="'Current location: ' + fullPath + '. Jump to folder'">
        <i class="far fa-folder rx-phone__path-icon"></i>
        <span class="rx-phone__path-text">
          <template v-for="(seg, i) in displaySegments">
            <span v-if="i > 0" :key="'s' + i" class="rx-phone__sep"> › </span>
            <b v-if="i === displaySegments.length - 1" :key="'t' + i">{{ seg }}</b>
            <span v-else :key="'t' + i">{{ seg }}</span>
          </template>
        </span>
        <i class="fas fa-chevron-down rx-phone__path-caret"></i>
      </button>
    </header>

    <main :class="['rx-phone__body', hasFab ? 'rx-phone__body--fab' : '']">
      <div class="rx-phone__summary">
        <span>{{ summary }}</span>
        <button type="button" class="rx-phone__sort" @click="$emit('toggle-sort')">Sort: {{ sortKey === 'modified' ? 'Modified' : 'Name' }}</button>
      </div>

      <div class="rx-phone__card">
        <div v-if="loading && items.length === 0" class="rx-phone__empty">Loading…</div>
        <div v-else-if="items.length === 0" class="rx-phone__empty">This folder is empty</div>
        <div
          v-for="item in items"
          :key="item.id"
          :class="['rx-phone__row', item.isArchived ? 'is-archived' : '', selectMode && selected[item.id] ? 'is-selected' : '']"
          :role="selectMode ? 'checkbox' : 'button'"
          :aria-checked="selectMode ? String(!!selected[item.id]) : null"
          :aria-disabled="selectMode && !selectable(item) ? 'true' : null"
          tabindex="0"
          @touchstart="onTouchStart(item, $event)"
          @touchmove="onTouchMove"
          @touchend="cancelPress"
          @touchcancel="cancelPress"
          @contextmenu="onContextMenu"
          @click="onRowClick(item)"
          @keydown.enter="onRowClick(item)">
          <span
            v-if="selectMode"
            :class="['rx-phone__check', selected[item.id] ? 'is-on' : '', selectable(item) ? '' : 'is-off']"
            aria-hidden="true">
            <i v-if="selected[item.id]" class="fas fa-check"></i>
          </span>
          <span class="rx-phone__rowicon" aria-hidden="true">
            <svg v-if="item.kind === 'folder'" width="28" height="24" viewBox="0 0 28 24">
              <path d="M2 4.5A2.5 2.5 0 0 1 4.5 2h6.2l2.6 3h10.2A2.5 2.5 0 0 1 26 7.5v12A2.5 2.5 0 0 1 23.5 22h-19A2.5 2.5 0 0 1 2 19.5z" fill="#e3eef8" stroke="#2f6fa3" stroke-width="1.6" stroke-linejoin="round" />
            </svg>
            <svg v-else width="24" height="28" viewBox="0 0 24 28">
              <path d="M4 2h11l6 6v16.5A1.5 1.5 0 0 1 19.5 26h-15A1.5 1.5 0 0 1 3 24.5v-21A1.5 1.5 0 0 1 4 2z" fill="#f9fafb" stroke="#6b7280" stroke-width="1.6" stroke-linejoin="round" />
              <path d="M15 2v6h6" fill="none" stroke="#6b7280" stroke-width="1.6" stroke-linejoin="round" />
            </svg>
          </span>
          <span class="rx-phone__rowtext">
            <span class="rx-phone__nameline">
              <span class="rx-phone__name">{{ item.name }}</span>
              <span v-if="item.isArchived" class="rx-pill">Archived</span>
            </span>
            <span class="rx-phone__meta">{{ meta(item) }}</span>
          </span>
          <template v-if="!selectMode">
            <button
              v-if="actionable(item)"
              type="button"
              class="rx-phone__kebab rx-phone__kebab--active"
              :aria-label="'Actions for ' + item.name"
              @click.stop="$emit('actions', item)"
              @touchstart.stop
              @keydown.enter.stop>
              <i class="fas fa-ellipsis-v"></i>
            </button>
            <button v-else type="button" class="rx-phone__kebab" disabled title="No actions available" :aria-label="'Actions for ' + item.name" @click.stop>
              <i class="fas fa-ellipsis-v"></i>
            </button>
          </template>
        </div>
      </div>

      <p v-if="items.length > 0 && !selectMode && hasSelectable" class="rx-phone__hint">Long-press any row to start selecting.</p>
    </main>
  </div>
</template>

<script>
import { ROOT_ID, nodeMeta, hasItemActions, canSelect } from './resourcesData'
import { formatSize } from './fileVersions'

var LONG_PRESS_MS = 500
var MOVE_TOLERANCE = 10

export default {
  name: 'PhoneFolderView',
  props: {
    currentNode: { type: Object, required: true },
    pathNodes: { type: Array, required: true },
    items: { type: Array, required: true },
    loading: { type: Boolean, default: false },
    // Leave room at the bottom for the floating action button / select action bar
    hasFab: { type: Boolean, default: false },
    sortKey: { type: String, default: 'name' },
    selectMode: { type: Boolean, default: false },
    selected: { type: Object, default: function () { return {} } }
  },
  computed: {
    hasSelectable () {
      return this.items.some(function (i) { return canSelect(i) })
    },
    selectedCount () {
      var sel = this.selected
      return this.items.filter(function (i) { return !!sel[i.id] }).length
    },
    isRoot () {
      return this.currentNode.id === ROOT_ID
    },
    fullPath () {
      return this.pathNodes.map(function (n) { return n.name }).join(' › ')
    },
    displaySegments () {
      var names = this.pathNodes.map(function (n) { return n.name })
      // Truncate middle segments: Resources › … › Parent › Current
      if (names.length > 4) {
        return [names[0], '…'].concat(names.slice(names.length - 2))
      }
      return names
    },
    summary () {
      var folders = 0
      var files = 0
      this.items.forEach(function (i) {
        if (i.kind === 'folder') folders++
        else files++
      })
      var parts = [folders + (folders === 1 ? ' folder' : ' folders')]
      if (files > 0 || !this.isRoot) parts.push(files + (files === 1 ? ' file' : ' files'))
      return parts.join(' · ')
    }
  },
  created () {
    this.pressTimer = null
    this.pressStart = null
    this.suppressClick = false
  },
  beforeDestroy () {
    this.cancelPress()
  },
  methods: {
    actionable (item) {
      return hasItemActions(item)
    },
    selectable (item) {
      return canSelect(item)
    },
    meta (item) {
      var text = nodeMeta(item, 'short')
      // F4b: size in the secondary line when known (legacy files have none)
      if (item.kind === 'file' && item.raw && item.raw.sizeBytes !== null && item.raw.sizeBytes !== undefined) {
        text += ' · ' + formatSize(item.raw.sizeBytes)
      }
      return text
    },
    onRowClick (item) {
      if (this.suppressClick) {
        this.suppressClick = false
        return
      }
      if (this.selectMode) {
        if (canSelect(item)) this.$emit('toggle-select', item)
        return
      }
      this.$emit('open', item)
    },
    // Long-press (500ms) starts select mode; moving > 10px (scrolling) cancels it.
    onTouchStart (item, e) {
      var self = this
      this.cancelPress()
      this.suppressClick = false
      if (this.selectMode || !canSelect(item)) return
      var t = e.touches && e.touches[0]
      if (!t) return
      this.pressStart = { x: t.clientX, y: t.clientY }
      this.pressTimer = setTimeout(function () {
        self.pressTimer = null
        self.suppressClick = true
        self.$emit('select-start', item)
      }, LONG_PRESS_MS)
    },
    onTouchMove (e) {
      if (!this.pressTimer || !this.pressStart) return
      var t = e.touches && e.touches[0]
      if (!t) return
      if (Math.abs(t.clientX - this.pressStart.x) > MOVE_TOLERANCE || Math.abs(t.clientY - this.pressStart.y) > MOVE_TOLERANCE) {
        this.cancelPress()
      }
    },
    cancelPress () {
      if (this.pressTimer) clearTimeout(this.pressTimer)
      this.pressTimer = null
      this.pressStart = null
    },
    // Avoid the native long-press context menu while a long-press is pending or just fired
    onContextMenu (e) {
      if (this.pressTimer || this.suppressClick) e.preventDefault()
    }
  }
}
</script>

<style scoped>
.rx-phone {
  min-height: 100%;
  background: #f3f4f6;
  overflow-x: hidden;
}
.rx-phone__header {
  background: #16243f;
  color: #fff;
  padding: 6px 12px 12px;
}
.rx-phone__bar {
  display: flex;
  align-items: center;
  min-height: 48px;
}
.rx-phone__iconbtn {
  flex: 0 0 44px;
  width: 44px;
  height: 44px;
  border: 0;
  background: transparent;
  color: #fff;
  font-size: 18px;
  padding: 0;
}
.rx-phone__title {
  flex: 1 1 auto;
  min-width: 0;
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: #fff;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.rx-phone__title--root {
  padding-left: 4px;
}
.rx-phone__select {
  flex: 0 0 auto;
  min-width: 44px;
  height: 44px;
  border: 0;
  background: transparent;
  color: #fff;
  font-size: 16px;
  opacity: 0.6;
  padding: 0 4px;
}
.rx-phone__path {
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 44px;
  margin-top: 4px;
  padding: 8px 12px;
  border: 0;
  border-radius: 10px;
  background: #2a3a5c;
  color: #fff;
  font-size: 15px;
  text-align: left;
}
.rx-phone__path-icon {
  flex: 0 0 auto;
  margin-right: 8px;
}
.rx-phone__path-text {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.rx-phone__sep {
  opacity: 0.7;
}
.rx-phone__path-caret {
  flex: 0 0 auto;
  margin-left: 8px;
  font-size: 12px;
}
.rx-phone__body {
  padding: 12px;
}
.rx-phone__body--fab {
  padding-bottom: calc(96px + env(safe-area-inset-bottom));
}
.rx-phone__summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 14px;
  color: #555;
  margin-bottom: 8px;
}
.rx-phone__sort {
  border: 0;
  background: transparent;
  color: #1a7f86;
  font-size: 14px;
  font-weight: 600;
  min-height: 44px;
  padding: 0 4px;
}
.rx-phone__card {
  background: #fff;
  border: 1px solid #e2e5ea;
  border-radius: 12px;
  overflow: hidden;
}
.rx-phone__row {
  display: flex;
  align-items: center;
  min-height: 64px;
  padding: 8px 4px 8px 12px;
  border-bottom: 1px solid #eef0f2;
  cursor: pointer;
}
.rx-phone__row:last-child {
  border-bottom: 0;
}
.rx-phone__row:active {
  background: #f7f8fa;
}
.rx-phone__rowicon {
  flex: 0 0 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
}
.rx-phone__rowtext {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.rx-phone__name {
  font-size: 17px;
  font-weight: 500;
  color: #1f2937;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.rx-phone__meta {
  font-size: 14px;
  color: #6b7280;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.rx-phone__kebab {
  flex: 0 0 44px;
  width: 44px;
  height: 44px;
  border: 0;
  background: transparent;
  color: #6b7280;
  opacity: 0.6;
}
.rx-phone__kebab--active {
  opacity: 1;
  color: #4b5563;
}
.rx-phone__empty {
  padding: 24px 16px;
  text-align: center;
  color: #6b7280;
  font-size: 14px;
}
.rx-phone__hint {
  margin: 12px 4px 0;
  font-size: 14px;
  color: #6b7280;
}
.rx-phone__select--on {
  opacity: 1;
  font-weight: 600;
}
.rx-phone__select:disabled {
  opacity: 0.5;
}
.rx-phone__row {
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  user-select: none;
}
.rx-phone__row.is-selected {
  background: #e3f1ef;
}
.rx-phone__row.is-archived .rx-phone__rowicon,
.rx-phone__row.is-archived .rx-phone__rowtext {
  opacity: 0.55;
}
.rx-phone__check {
  flex: 0 0 28px;
  width: 28px;
  height: 28px;
  margin-right: 12px;
  border: 2px solid #9ca3af;
  border-radius: 50%;
  background: #fff;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
}
.rx-phone__check.is-on {
  background: #1a7f86;
  border-color: #1a7f86;
}
.rx-phone__check.is-off {
  border-style: dashed;
  border-color: #d1d5db;
  background: #f3f4f6;
}
.rx-phone__nameline {
  display: flex;
  align-items: center;
  min-width: 0;
}
.rx-pill {
  flex: 0 0 auto;
  margin-left: 8px;
  padding: 2px 8px;
  border-radius: 999px;
  background: #e5e7eb;
  color: #4b5563;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.4;
}
</style>
